import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { useStoreSettings } from '../contexts/StoreSettingsContext';
import { COUNTRY_ISD_CODES, getPhoneRuleByIsd } from '../constants/countryIsdCodes';

const Register = () => {
  const [userId, setUserId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [countryIsdCode, setCountryIsdCode] = useState('+91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [userIdStatus, setUserIdStatus] = useState('');
  const [userIdSuggestions, setUserIdSuggestions] = useState([]);
  const [checkingUserId, setCheckingUserId] = useState(false);
  const { register, checkUserIdAvailability } = useAuth();
  const { settings: storeSettings } = useStoreSettings();
  const navigate = useNavigate();
  const canSelectOtherIsd = Boolean(storeSettings.allow_non_india_user_isd);
  const phoneRule = getPhoneRuleByIsd(countryIsdCode);
  const phoneLengthHint = phoneRule.minLength === phoneRule.maxLength
    ? `${phoneRule.minLength} digits`
    : `${phoneRule.minLength}-${phoneRule.maxLength} digits`;

  useEffect(() => {
    if (!canSelectOtherIsd) {
      setCountryIsdCode('+91');
    }
  }, [canSelectOtherIsd]);

  const normalizedUserId = userId.trim().toLowerCase();

  const checkAvailability = async (candidate) => {
    const cleaned = (candidate || '').trim().toLowerCase();
    if (!cleaned) {
      setUserIdStatus('');
      setUserIdSuggestions([]);
      return true;
    }
    if (!/^[a-z0-9]{4,30}$/.test(cleaned)) {
      setUserIdStatus('User ID must be 4-30 letters/numbers only.');
      setUserIdSuggestions([]);
      return false;
    }

    setCheckingUserId(true);
    const result = await checkUserIdAvailability(cleaned);
    setCheckingUserId(false);

    if (!result.success) {
      setUserIdStatus(result.message || 'Could not check user ID right now.');
      setUserIdSuggestions([]);
      return false;
    }

    if (result.available) {
      setUserIdStatus('User ID is available');
      setUserIdSuggestions([]);
      return true;
    }

    setUserIdStatus('User ID is not available');
    setUserIdSuggestions(result.suggestions || []);
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (!/^[a-z0-9]{4,30}$/.test(normalizedUserId)) {
      setError('User ID must be 4-30 letters/numbers only.');
      setLoading(false);
      return;
    }

    const isUserIdAvailable = await checkAvailability(normalizedUserId);
    if (!isUserIdAvailable) {
      setError('Please choose an available User ID.');
      setLoading(false);
      return;
    }

    const cleanPhone = phoneNumber.replace(/\D/g, '');
    if (cleanPhone.length < phoneRule.minLength || cleanPhone.length > phoneRule.maxLength) {
      setError(`Enter a valid mobile number for ${countryIsdCode} (${phoneLengthHint})`);
      setLoading(false);
      return;
    }

    const result = await register({
      userId: normalizedUserId,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      password,
      countryIsdCode,
      phoneNumber: cleanPhone,
    });

    if (result.success) {
      setSuccess(result.message);
      setTimeout(() => navigate('/login'), 2000);
    } else {
      const detail = result.message;
      if (detail && typeof detail === 'object' && detail.message) {
        setError(detail.message);
        if (Array.isArray(detail.suggestions)) {
          setUserIdSuggestions(detail.suggestions);
          setUserIdStatus('Try one of the suggested User IDs');
        }
      } else {
        setError(result.message);
      }
    }

    setLoading(false);
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={6} lg={5}>
          <Card>
            <Card.Header className="text-center">
              <h3>Create Account</h3>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>User ID</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Choose your user ID"
                    value={userId}
                    onChange={(e) => {
                      const cleaned = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
                      setUserId(cleaned);
                      setUserIdStatus('');
                      setUserIdSuggestions([]);
                    }}
                    onBlur={() => checkAvailability(userId)}
                    required
                  />
                  <Form.Text className="text-muted">
                    Use 4-30 letters and numbers only.
                  </Form.Text>
                  {checkingUserId && <div className="small text-muted mt-1">Checking availability...</div>}
                  {userIdStatus && (
                    <div className={`small mt-1 ${userIdStatus === 'User ID is available' ? 'text-success' : 'text-danger'}`}>
                      {userIdStatus}
                    </div>
                  )}
                  {userIdSuggestions.length > 0 && (
                    <div className="small mt-2">
                      Suggested: {userIdSuggestions.map((suggestion, idx) => (
                        <Button
                          key={suggestion}
                          variant="link"
                          type="button"
                          size="sm"
                          className="p-0 me-2"
                          onClick={() => {
                            setUserId(suggestion);
                            setUserIdStatus('');
                            setUserIdSuggestions([]);
                          }}
                        >
                          {suggestion}{idx < userIdSuggestions.length - 1 ? ',' : ''}
                        </Button>
                      ))}
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter first name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Email Address (Optional)</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Add email if you want to log in with email/password or receive User ID by email.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Country ISD Code</Form.Label>
                  <Form.Select
                    value={countryIsdCode}
                    onChange={(e) => setCountryIsdCode(e.target.value)}
                    disabled={!canSelectOtherIsd}
                    required
                  >
                    {COUNTRY_ISD_CODES.map((entry) => (
                      <option key={entry.code} value={entry.code}>
                        {entry.label}
                      </option>
                    ))}
                  </Form.Select>
                  {!canSelectOtherIsd && (
                    <Form.Text className="text-muted">
                      Registration is currently limited to India (+91).
                    </Form.Text>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Mobile Number</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="Enter mobile number"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, phoneRule.maxLength))}
                    required
                  />
                  <Form.Text className="text-muted">
                    Required for {countryIsdCode}: {phoneLengthHint}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Create a password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength="6"
                  />
                  <Form.Text className="text-muted">
                    Password must be at least 6 characters long.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Confirm Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </Form.Group>

                {error && (
                  <Alert variant="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert variant="success" className="mb-3">
                    {success}
                  </Alert>
                )}

                <div className="d-grid">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading}
                    size="lg"
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Creating Account...
                      </>
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
            <Card.Footer className="text-center">
              <p className="mb-0">
                Already have an account?{' '}
                <Link to="/login" className="text-decoration-none">
                  Login here
                </Link>
              </p>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Register;